import React from 'react';
import { ArrowLeft, RotateCcw, TriangleAlert } from 'lucide-react';
import { Skeleton } from '../../../Common/Skeleton';
import { Button, Card } from './primitives';

/*
 * Loading and failure states.
 *
 * The screen previously had neither: a failed fetch resolved to an empty array,
 * which the page read as "still loading", so a bad id or a dropped connection
 * produced a spinner that never stopped. Both states are now explicit and the
 * failure one offers a way out.
 *
 * Loading is a skeleton of the real layout rather than a centred spinner, so
 * the page doesn't jump when data lands.
 */

const BackLink = ({ onBack }) => (
  <button
    type="button"
    onClick={onBack}
    className="inline-flex items-center gap-1.5 mb-4 bg-transparent border-0 p-0 text-[12.5px] text-nysmah-muted hover:text-nysmah-dark cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent rounded"
  >
    <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
    Back to orders
  </button>
);

export const OrderViewSkeleton = ({ onBack }) => (
  <div aria-busy="true" aria-live="polite">
    <BackLink onBack={onBack} />
    <span className="sr-only">Loading order…</span>

    <div className="flex items-center gap-3 mb-4">
      <Skeleton className="h-6 w-36" />
      <Skeleton className="h-5 w-24 rounded-full" />
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>

    <Skeleton className="h-16 w-full mb-4 rounded-xl" />

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 flex flex-col gap-4">
        <Skeleton className="h-56 w-full rounded-xl" />
        <Skeleton className="h-36 w-full rounded-xl" />
      </div>
      <div className="flex flex-col gap-4">
        <Skeleton delay={1} className="h-44 w-full rounded-xl" />
        <Skeleton delay={2} className="h-24 w-full rounded-xl" />
        <Skeleton delay={3} className="h-40 w-full rounded-xl" />
      </div>
    </div>
  </div>
);

export const OrderViewError = ({ message, onRetry, onBack }) => (
  <div>
    <BackLink onBack={onBack} />
    <Card className="max-w-md mx-auto mt-12">
      <div className="px-5 py-8 flex flex-col items-center text-center gap-3">
        <span
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#FCF2F2] text-[#C2453D]"
          aria-hidden="true"
        >
          <TriangleAlert className="w-5 h-5" />
        </span>
        <div>
          <h2 className="text-[15px] font-semibold text-nysmah-dark m-0">
            {message || 'Could not load this order.'}
          </h2>
          <p className="text-[12.5px] text-nysmah-muted mt-1 m-0">
            It may have been removed, or the connection dropped.
          </p>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Button variant="primary" onClick={() => onRetry()}>
            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
            Try again
          </Button>
          <Button variant="ghost" onClick={onBack}>
            Back to orders
          </Button>
        </div>
      </div>
    </Card>
  </div>
);
